import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CloudIcon from "@mui/icons-material/Cloud";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import SupportIcon from "@mui/icons-material/Support";
import EventIcon from "@mui/icons-material/Event";
import CancelIcon from "@mui/icons-material/Cancel";
import CircularProgress from "@mui/material/CircularProgress";
import { API_BASE_URL } from '../../../../../services/config.js';
import { AuthContext } from 'context';

const translateStatus = (status) => {
  switch (status) {
    case 'active': return 'Aktiv';
    case 'pending': return 'Ausstehend';
    case 'inactive': return 'Inaktiv';
    default: return status || 'Unbekannt';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'success';
    case 'pending': return 'warning';
    default: return 'error';
  }
};

const getServiceTypeLabel = (serviceType) => {
  const labels = {
    'windows-server': 'Windows Server',
    'linux-server': 'Linux Server',
    'webserver': 'Webserver',
    's3-storage': 'S3 Objektspeicher',
    'cloud-storage': 'Cloud Storage',
    'private-cloud': 'Private Cloud',
    'private-network': 'Private Network',
    'pbx': 'PBX Cloud-Telefonanlage',
    'game-server': 'Game Server',
    'domain': 'Domain',
  };
  return labels[serviceType] || serviceType || 'IaaS Service';
};

function IaasDetail() {
  const { serviceId } = useParams();
  const { user } = useContext(AuthContext);
  const [service, setService] = useState(null);
  const [childServices, setChildServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/iaas-service/${serviceId}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Service-Details: ${response.statusText}`);
        }

        const data = await response.json();
        setService(data);

        if (data && data.service_type === 'webserver') {
          try {
            const allServicesResponse = await fetch(`${API_BASE_URL}/iaas-service`, {
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' }
            });

            if (allServicesResponse.ok) {
              const allServices = await allServicesResponse.json();
              const linkedDomains = allServices.filter(s =>
                s.parent_service_id === data.id && s.service_type === 'domain'
              );
              setChildServices(linkedDomains);
            }
          } catch (domainError) {
            console.error("Fehler beim Laden der verknüpften Domains:", domainError);
          }
        }
      } catch (err) {
        setError(err.message);
        console.error("Fehler beim Laden der Service-Details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (serviceId) {
      fetchServiceDetails();
    }
  }, [serviceId]);

  const handleOpenServiceConsole = () => {
    const serviceUrl = `https://cloud.uds-solutions.de/${serviceId}`;
    window.open(serviceUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenDocumentation = () => {
    window.open(`https://uds-solutions.de/services/${service?.service_type || 'cloud'}`, "_blank", "noopener,noreferrer");
  };

  const handleCreateSupportTicket = () => {
    window.open(`/Support?serviceId=${serviceId}&type=${service?.service_type || 'cloud'}`, "_blank", "noopener,noreferrer");
  };

  const handleBookAppointment = () => {
    window.open("https://outlook.office365.com/owa/calendar/IhreEinrichtungdurchUDSSolutions@uds-solutions.de/bookings/", "_blank", "noopener,noreferrer");
  };

  const handleCancelService = () => {
    window.open("https://forms.office.com/e/BF6w5pUKvq", "_blank", "noopener,noreferrer");
  };

  const renderSpecs = () => {
    const serviceType = service?.service_type;

    if (['windows-server', 'linux-server', 'private-cloud'].includes(serviceType)) {
      return (
        <>
          {service?.server_cores && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">CPU:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_cores} vCPUs</SoftTypography>
            </SoftBox>
          )}
          {service?.server_ram && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">RAM:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_ram} GB</SoftTypography>
            </SoftBox>
          )}
          {service?.server_storage && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Speicher:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_storage} GB SSD</SoftTypography>
            </SoftBox>
          )}
          {serviceType === 'windows-server' && service?.windows_version && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Windows Version:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">Windows Server {service.windows_version}</SoftTypography>
            </SoftBox>
          )}
          {serviceType === 'linux-server' && service?.linux_distribution && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Distribution:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.linux_distribution}</SoftTypography>
            </SoftBox>
          )}
          {service?.rds_licenses > 0 && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">RDS CALs:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.rds_licenses}</SoftTypography>
            </SoftBox>
          )}
          {service?.backup_enabled && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Backup:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular" color="success">Aktiviert</SoftTypography>
            </SoftBox>
          )}
        </>
      );
    }

    if (['s3-storage', 'cloud-storage'].includes(serviceType)) {
      return service?.server_storage ? (
        <SoftBox mb={1}>
          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Speicher:</SoftTypography>{" "}
          <SoftTypography variant="button" fontWeight="regular">{service.server_storage} GB</SoftTypography>
        </SoftBox>
      ) : null;
    }

    if (serviceType === 'webserver') {
      return (
        <>
          {service?.server_storage && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Webspace:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_storage} GB</SoftTypography>
            </SoftBox>
          )}
          {service?.domain_name && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Domain:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.domain_name}</SoftTypography>
            </SoftBox>
          )}
        </>
      );
    }

    if (serviceType === 'game-server') {
      return (
        <>
          {service?.server_cores && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">CPU:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_cores} vCPUs</SoftTypography>
            </SoftBox>
          )}
          {service?.server_ram && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">RAM:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_ram} GB</SoftTypography>
            </SoftBox>
          )}
          {service?.server_storage && (
            <SoftBox mb={1}>
              <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">Speicher:</SoftTypography>{" "}
              <SoftTypography variant="button" fontWeight="regular">{service.server_storage} GB</SoftTypography>
            </SoftBox>
          )}
        </>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress color="info" />
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox my={3} textAlign="center">
          <SoftTypography variant="h5" color="error">
            {error}
          </SoftTypography>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container spacing={3}>
          {/* Service Informationen */}
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <SoftBox display="flex" flexDirection="column" height="100%">
                      <SoftBox display="flex" alignItems="center" mb={1}>
                        <CloudIcon fontSize="large" color="info" sx={{ mr: 1 }} />
                        <SoftTypography variant="h5" fontWeight="bold">
                          {service?.servicename || getServiceTypeLabel(service?.service_type)}
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          ID:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular">
                          {service?.id || serviceId}
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Typ:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular">
                          {getServiceTypeLabel(service?.service_type)}
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Hostname:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular">
                          {service?.hostname}
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Status:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular" color={getStatusColor(service?.sstatus)}>
                          {translateStatus(service?.sstatus)}
                        </SoftTypography>
                      </SoftBox>

                      {service?.verlaengerungsdatum && (
                        <SoftBox mb={1}>
                          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                            Verlängerung:
                          </SoftTypography>{" "}
                          <SoftTypography variant="button" fontWeight="regular">
                            {new Date(service.verlaengerungsdatum).toLocaleDateString('de-DE')}
                          </SoftTypography>
                        </SoftBox>
                      )}
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <SoftBox display="flex" flexDirection="column" height="100%">
                      <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                        Ausstattung
                      </SoftTypography>

                      {renderSpecs()}

                      <SoftBox mb={1} mt={2}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Preis:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular">
                          {service?.endcustomer_price && user?.id !== service?.partnerId
                            ? `${parseFloat(service.endcustomer_price).toFixed(2)} €/Monat`
                            : service?.price
                              ? `${parseFloat(service.price).toFixed(2)} €/Monat`
                              : 'Auf Anfrage'}
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox mt="auto" pt={3}>
                        <Stack spacing={1} direction="row" flexWrap="wrap">
                          <SoftButton
                            variant="gradient"
                            color="info"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenServiceConsole}
                          >
                            Service öffnen
                          </SoftButton>
                          <SoftButton
                            variant="outlined"
                            color="info"
                            startIcon={<DescriptionIcon />}
                            onClick={handleOpenDocumentation}
                          >
                            Dokumentation
                          </SoftButton>
                          <SoftButton
                            variant="outlined"
                            color="dark"
                            startIcon={<SupportIcon />}
                            onClick={handleCreateSupportTicket}
                          >
                            Support
                          </SoftButton>
                        </Stack>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>

          {/* Domains Anzeige für Webserver */}
          {service?.service_type === 'webserver' && (
            <Grid item xs={12}>
              <Card>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                    Domains
                  </SoftTypography>

                  {childServices.length > 0 ? (
                    <Grid container spacing={2}>
                      {childServices.map((domain) => (
                        <Grid item xs={12} sm={6} md={4} key={domain.id}>
                          <Card sx={{ boxShadow: 'none', border: '1px solid #eee' }}>
                            <SoftBox p={2}>
                              <SoftTypography variant="button" fontWeight="bold">
                                {domain.domain_name || domain.hostname}
                              </SoftTypography>
                              <SoftBox mt={1} display="flex" justifyContent="space-between" alignItems="center">
                                <SoftTypography variant="caption" color="text">
                                  Status: <span style={{ color: domain.sstatus === 'active' ? 'green' : 'orange' }}>
                                    {translateStatus(domain.sstatus)}
                                  </span>
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text">
                                  {domain.price} €/Jahr
                                </SoftTypography>
                              </SoftBox>
                            </SoftBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <SoftBox textAlign="center" py={2}>
                      <SoftTypography variant="button" color="text">
                        Keine Domains gefunden
                      </SoftTypography>
                    </SoftBox>
                  )}

                  <SoftBox mt={3} display="flex" justifyContent="flex-end">
                    <SoftButton
                      variant="gradient"
                      color="info"
                      onClick={() => window.location.href = `/iaas/products/uds-webserver/configur?mode=add&webserverId=${service.id}`}
                    >
                      Domain hinzufügen
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
          )}

          {/* Managed Service Information */}
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="medium" mb={1}>
                        Managed Service
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Dieser Service wird von unserem Team vollständig verwaltet und betreut. Unser Expertenteam kümmert sich um Wartung, Updates, Sicherheit und Monitoring.
                      </SoftTypography>
                      <SoftBox mt={2}>
                        <SoftTypography variant="body2" color="text">
                          Um Ihren Service optimal einzurichten, empfehlen wir einen Einrichtungstermin mit unserem Team zu buchen.
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }}}>
                    <SoftButton
                      variant="gradient"
                      color="success"
                      startIcon={<EventIcon />}
                      onClick={handleBookAppointment}
                    >
                      Einrichtungstermin buchen
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>

          {/* Service Kündigen */}
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="medium" mb={1} color="error">
                        Service Kündigen
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Wenn Sie diesen Service kündigen möchten, beachten Sie bitte folgende Konsequenzen:
                      </SoftTypography>
                      <SoftBox component="ul" pl={2} mt={1} mb={1}>
                        <SoftTypography component="li" variant="body2" color="text">
                          Alle Daten und Konfigurationen werden nach Ablauf der Kündigungsfrist unwiderruflich gelöscht.
                        </SoftTypography>
                        <SoftTypography component="li" variant="body2" color="text">
                          Die Kündigung wird zum Ende des aktuellen Abrechnungszeitraums wirksam.
                        </SoftTypography>
                        <SoftTypography component="li" variant="body2" color="text">
                          Stellen Sie sicher, dass Sie alle wichtigen Daten vor der Kündigung gesichert haben.
                        </SoftTypography>
                      </SoftBox>
                      <SoftTypography variant="body2" color="text" fontWeight="medium">
                        Ablauf der Kündigung:
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Wir prüfen die Anfrage und geben per Mail die Rückmeldung.
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }}}>
                    <SoftButton
                      variant="gradient"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelService}
                    >
                      Service kündigen
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default IaasDetail;
